import React, { useMemo } from 'react'
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  TimeScale,
  Tooltip,
  Plugin,
  ChartOptions,
  ChartData,
  ChartDataset
} from 'chart.js'
import { Chart, } from 'react-chartjs-2'
import annotationPlugin from 'chartjs-plugin-annotation'
import 'chartjs-adapter-moment'
import { useGlobalState } from 'state'
import { SuijinStatus } from 'common/consts'
import { ObservatoryRow, ObservatoryValue } from 'common/types'
import dayjs from 'dayjs'
import { blue, grey } from '@mui/material/colors'

ChartJS.register(
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  TimeScale,
  Tooltip,
  annotationPlugin
)

type Props = {
  height: number
  timeSeries: ObservatoryValue[]
  waterBoxLevel: number | null
}

export const UnderpassChart = ({height, timeSeries, waterBoxLevel}: Props) => {
  const state = useGlobalState()
  const [chartData, setChartData] = React.useState<ChartData>({labels: [], datasets: []})
  const [chartOptions, setChartOptions] = React.useState<ChartOptions>({})
  const chartRef = React.useRef<ChartJS>(null)

  const maxLevel = 450
  const minLevel = -50

  React.useEffect(() => {
    const handleVisibilityChange = () => {
      chartRef.current?.update()
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  const plugins: Plugin[] = React.useMemo(() => {
    const landform = [
      [0, -50],
      [0, 400],
      [5, 400],
      [5, 20],
      [95, 20],
      [95, 400],
      [100, 400],
      [100, -50],
    ]

    return [{
      id: 'landform',
      beforeDatasetsDraw: chart => {
        if (!chart.scales.xDate || !chart.scales.yLevels) {
          return
        }
        const ctx = chart.ctx
        const xAxe = chart.scales.xDate
        const yAxe = chart.scales.yLevels
        const arrX = landform.map(lf => lf[0])
        const arrY = landform.map(lf => lf[1])
        const minX = Math.min(...arrX)
        const maxX = Math.max(...arrX)
        const maxY = Math.max(...arrY)

        const getX = (baseX: number): number => {
          return ((baseX - minX) / (maxX - minX)) * xAxe.width + xAxe.left
        }

        const getY = (baseY: number): number => {
          return ((yAxe.max - yAxe.min - (baseY - yAxe.min)) / (yAxe.max - yAxe.min)) * yAxe.height + yAxe.top
        }

        // landform
        ctx.beginPath()
        for (let i = 0; i < landform.length; i++) {
          ctx.lineTo(getX(landform[i][0]), getY(landform[i][1]))
        }
        ctx.closePath()
        ctx.fillStyle = grey[300]
        ctx.fill()

        // 道路面
        // const road_offset = 10
        // const road_y = getY(0 - road_offset / 2)
        // for (let i = 1; i < landform.length; i++) {
        //   if (landform[i - 1][1] !== 0 || landform[i][1] !== 0) continue
        //   ctx.strokeStyle = grey[700]
        //   ctx.lineWidth = getY(0) - getY(road_offset)
        //   ctx.beginPath()
        //   ctx.setLineDash([])
        //   ctx.moveTo(getX(landform[i - 1][0]), road_y)
        //   ctx.lineTo(getX(landform[i][0]), road_y)
        //   ctx.stroke()
        // }

        // 線形グラデーションを作成
        // if (observatory.sensors && waterBoxLevel !== null) {
        //   const sensorMaxLevel = Math.max(...observatory.sensors.map(s => s.level))
        //   if (sensorMaxLevel === waterBoxLevel) {
        //     const gradientHeight = 0.6
        //     const gradient = ctx.createLinearGradient(
        //       0,
        //       getY(gradientHeight),
        //       0,
        //       getY(sensorMaxLevel)
        //     )
        //     gradient.addColorStop(0, '#3D8AFF00')
        //     gradient.addColorStop(1, '#3D8AFFFF')
        //     ctx.fillStyle = gradient
        //     ctx.fillRect(
        //       xAxe.left,
        //       getY(gradientHeight),
        //       xAxe.right - xAxe.left,
        //       getY(sensorMaxLevel) - getY(gradientHeight)
        //     )
        //   }
        // }

        // 画像
        if (state.app.images.suijinChartCar) {
          const img = state.app.images.suijinChartCar
          const h = getY(0) - getY(150)
          const w = h * (img.width / img.height)
          ctx.drawImage(
            img,
            getX(10),
            getY(20) - h,
            w,
            h
          )
        }
      }
    }]
  }, [waterBoxLevel])

  React.useEffect(() => {
    loadTimeSeries()
  }, [timeSeries, waterBoxLevel])

  const loadTimeSeries = () => {
    const targetTimeSeries = timeSeries.filter(ts => ts.level !== undefined)
    const labels: string[] = targetTimeSeries.map(ts => {
      return dayjs.utc(ts.date).local().format('YYYY/MM/DD HH:mm')
    })

    const datasets: ChartDataset[] = []

    const data = targetTimeSeries.map(ts => ts.level === undefined ? null : ts.level)
    datasets.push({
      type: 'line' as const,
      label: '',
      pointBackgroundColor: 'white',
      fill: false,
      xAxisID: 'xDate',
      yAxisID: 'yLevels',
      borderWidth: 1,
      borderColor: '#01163F',
      backgroundColor: '#01163F',
      pointBorderColor: '#01163F',
      pointRadius: 3,
      pointHitRadius: 0,
      data: data,
      spanGaps: true
    })

    setChartOptions({
      responsive: true,
      animation: false,
      interaction: {
        mode: 'index',
        intersect: false
      },
      scales: {
        xDate: {
          type: 'time',
          time: {
            tooltipFormat: 'MM/DD HH:mm',
            parser: 'YYYY/MM/DD HH:mm',
            unit: 'hour',
            minUnit: 'minute',
            displayFormats: {
              'hour': 'HH:mm',
            }
          },
          min: state.app.baseDate.subtract(3, 'hours').local().format('YYYY/MM/DD HH:mm'),
          max: state.app.baseDate.add(10, 'minutes').local().format('YYYY/MM/DD HH:mm')
        },
        yLevels: {
          ticks: {
            stepSize: 50
          },
          max: maxLevel,
          min: minLevel,
        }
      },
      plugins: {
        filler: {
          drawTime: 'beforeDatasetsDraw'
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              let label = context.dataset.label || ''
              if (label) {
                label += ': '
              }
              if (context.parsed.y !== null) {
                label += context.parsed.y + 'm'
              }
              return label
            }
          }
        },
        annotation: {
          annotations: {
            nowLine: {
              borderColor: '#FF0000',
              borderWidth: 1.5,
              type: 'line',
              scaleID: 'xDate',
              value: state.app.baseDate.local().format('YYYY/MM/DD HH:mm'),
              label: {
                content: '現在',
                position: 'start',
                display: true
              }
            },
            waterBox: {
              type: 'box',
              drawTime: 'beforeDatasetsDraw',
              yScaleID: 'yLevels',
              yMin: minLevel,
              yMax: waterBoxLevel !== null ? waterBoxLevel : minLevel,
              backgroundColor: blue[400],
            },
          }
        }
      }
    })

    setChartData({
      labels,
      datasets: datasets
    })
  }

  return <Chart ref={chartRef} type='bar' data={chartData} height={height} options={chartOptions} plugins={plugins} redraw />
}
